import { Injectable } from '@angular/core';
import { FunctionalError } from 'src/app/shared/errors/functional.error';

interface Context {
    [name: string]: any;
}

@Injectable({
    providedIn: 'root'
})
export class JsonMapperService {
    constructor() {}

    private static getJsonMap(ctor: any): any | undefined {
        return (ctor as any).jsonMap;
    }

    /**
     * Serialize object to 'json object' using optional static jsonMap on the class.
     */
    serialize(obj: any, context?: Context): any {
        try {
            if (obj == null) {
                return obj;
            }
            if (Array.isArray(obj)) {
                return obj.map(_ => this.serialize(_));
            }
            if (typeof obj === 'object') {
                const ctor = obj.constructor;
                const map = JsonMapperService.getJsonMap(ctor);
                if (!map) {
                    // Shallow copy; callers should implement custom transforms if needed
                    return { ...obj };
                }
                const out: any = {};
                Object.keys(map).forEach(prop => {
                    const conf = map[prop];
                    const value = (obj as any)[prop];
                    if (value === undefined) {
                        return;
                    }
                    if (typeof conf === 'string') {
                        out[conf] = value;
                    } else if (conf && typeof conf === 'object') {
                        const key = conf.key || prop;
                        // Optionals: if value is null and field is optional, we keep null by default
                        // Undefined values are already skipped above
                        out[key] = conf.converter && conf.converter.serialize ? conf.converter.serialize(value, context) : value;
                    }
                });
                return out;
            }
            return obj;
        } catch (err) {
            throw new FunctionalError(FunctionalError.ERROR_CODE.JSON_MAPPING_ERROR_SERIALISATION, undefined, undefined, err);
        }
    }

    /** Serialize object to json string */
    serializeToString(obj: any, context?: Context): string {
        try {
            return JSON.stringify(this.serialize(obj, context));
        } catch (err) {
            throw new FunctionalError(FunctionalError.ERROR_CODE.JSON_MAPPING_ERROR_SERIALISATION, undefined, undefined, err);
        }
    }

    /**
     * Create a class instance from a plain object using optional static jsonMap.
     */
    deserializeObject<T>(obj: object | string, clazz: new () => T, context?: Context): T {
        if (typeof obj === 'string') {
            const trimmed = obj.trim();
            // Only parse when the string looks like a JSON object/array.
            // Plain strings (e.g., arrays of labels) must pass through as-is.
            if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
                obj = JSON.parse(obj);
            } else {
                return obj as any;
            }
        }

        // Allow primitives to pass through (e.g., ArrayClassConverterFactory used with Number/String)
        // This keeps mapper tolerant when backend returns arrays of ids or strings.
        if (obj === null || obj === undefined) {
            return obj as any;
        }
        if (typeof obj !== 'object') {
            return obj as any;
        }

        try {
            const instance = new clazz();
            const map = JsonMapperService.getJsonMap(clazz);
            if (!map) {
                Object.assign(instance as any, obj);
                return instance;
            }
            const input = obj as any;
            Object.keys(map).forEach(prop => {
                const conf = map[prop];
                if (typeof conf === 'string') {
                    (instance as any)[prop] = input[conf];
                } else if (conf && typeof conf === 'object') {
                    const key = conf.key || prop;
                    const raw = input[key];
                    // Optional/default handling
                    if (raw === undefined || raw === null) {
                        if (Object.prototype.hasOwnProperty.call(conf, 'default')) {
                            const def = (conf as any).default;
                            (instance as any)[prop] = typeof def === 'function' ? def() : def;
                            return;
                        }
                        if ((conf as any).optional) {
                            // Do not set the property (leave as undefined)
                            return;
                        }
                        // Else assign raw (undefined/null) as-is
                        (instance as any)[prop] = raw;
                        return;
                    }
                    (instance as any)[prop] = conf.converter && conf.converter.deserialize ? conf.converter.deserialize(raw, context) : raw;
                }
            });
            return instance;
        } catch (err) {
            throw new FunctionalError(FunctionalError.ERROR_CODE.JSON_MAPPING_ERROR_DESERIALISATION, undefined, undefined, err);
        }
    }

    /**
     * Map an array of plain objects to class instances
     */
    deserializeArray<T>(obj: object[] | string, clazz: new () => T, context?: Context): T[] {
        if (typeof obj === 'string') {
            obj = JSON.parse(obj);
        }

        try {
            return (obj as any[]).map(item => this.deserializeObject(item, clazz, context));
        } catch (err) {
            throw new FunctionalError(FunctionalError.ERROR_CODE.JSON_MAPPING_ERROR_DESERIALISATION, undefined, undefined, err);
        }
    }
}
